import React, { FC, Fragment, useCallback, useMemo } from 'react'
import { Box, Button, FormHelperText, Grid, makeStyles, Tooltip, Typography } from '@material-ui/core'
import { ReactComponent as PlusIcon } from 'assets/svg/PlustIcon.svg'
import { FieldArray, FormikProvider, useFormik } from 'formik'
import { IFeeDistributionOption, IFlexDisbursement } from 'typescript/interfaces/deals'
import * as yup from 'yup'
import DatePicker from 'UI/DatePicker/DatePicker'
import {
  FLEX_AMOUNT_MORE_THAN_ADVANCED_AMOUNT,
  FLEX_AMOUNT_LESS_THAN_ADVANCED_AMOUNT,
  INVALID_DATE_MESSAGE,
  INVALID_PRICE_FORMAT,
  REQUIRED_FIELD,
  DEAL_START_DATE_IN_PAST,
  DISBURSEMENT_DATE_IN_PAST,
  DISBURSEMENT_DATE_EARLIER,
} from 'constants/errors'
import { isBeforeDate, isDateInPast, isSameCalendarDate } from 'utils/dateUtils'
import { priceReg } from 'constants/regExp'
import convertDateForPicker from 'utils/convertDateForPicker'
import isValidDate from 'utils/isValidDate'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import { mathRoundingAmount } from 'utils/rounding'
import { convertToPriceFormat } from 'utils/formatters'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import DatePickerWithTooltip from 'components/DatePickerWithTooltip'

const useStyles = makeStyles(theme => ({
  icon: {
    '& path': {
      fill: theme.palette.info.contrastText,
    },
  },
  iconRemove: {
    transform: 'rotate(45deg)',
  },
}))

const getBankFeeGrossUpFactor = (
  advanceAmount: number,
  bankFee: number,
  syndicationFeeDistribution?: IFeeDistributionOption,
  extendType?: string
): number => {
  const payoffTotal = advanceAmount - bankFee

  if (extendType !== 'Reverse' || syndicationFeeDistribution !== 'Distribute' || bankFee <= 0 || payoffTotal <= 0) {
    return 1
  }

  return advanceAmount / payoffTotal
}

const buildValidationSchema = (
  persistedDealStartDate?: string,
  persistedSchedule: IFlexDisbursement[] = [],
  resolveGrossAmount: (disbursement: IFlexDisbursement) => number = ({ amount }) => Number(amount) || 0
) => yup.object({
  dealStartDate: yup
    .string()
    .nullable()
    .required(REQUIRED_FIELD)
    .test('dealStartDate', INVALID_DATE_MESSAGE, val => isValidDate(val))
    .test('dealStartDateNotPast', DEAL_START_DATE_IN_PAST,
      val => isSameCalendarDate(val, persistedDealStartDate) || !isDateInPast(val)),
  schedule: yup.array().of(
    yup.object().shape({
      date: yup
        .string()
        .nullable()
        .required(REQUIRED_FIELD)
        .test('date', INVALID_DATE_MESSAGE, val => isValidDate(val))
        .test('dateNotPast', DISBURSEMENT_DATE_IN_PAST, function (val) {
          const persistedDate = persistedSchedule.find(x => x.id === (this.parent as IFlexDisbursement).id)?.date
          return isSameCalendarDate(val, persistedDate) || !isDateInPast(val)
        })
        .test('dateNotEarlierDealStartDate', DISBURSEMENT_DATE_EARLIER, function (val) {
          const context = this as any;
          const rootValues = context.from[context.from.length - 1].value;
          
          const { dealStartDate } = rootValues;

          return isSameCalendarDate(val, dealStartDate) || !isBeforeDate(val, dealStartDate)
        }),
      amount: yup.string().required(REQUIRED_FIELD).matches(priceReg, INVALID_PRICE_FORMAT),
    })
  ),
  flexAmount: yup
    .number()
    .test('flexAmount', FLEX_AMOUNT_MORE_THAN_ADVANCED_AMOUNT, function (val) {
      const totalDisbursement = (this.parent.schedule as IFlexDisbursement[])
        .map(disbursement => Number(resolveGrossAmount(disbursement)) || 0)
        .reduce((a, b) => a + b, 0)
      const totalFlex = (Number(val) || 0) + totalDisbursement
      const advanceAmount = Number(this.parent.advanceAmount) || 0
      // Round to 2 decimal places to avoid floating point precision issues
      const roundedTotalFlex = mathRoundingAmount(totalFlex)
      const roundedAdvanceAmount = mathRoundingAmount(advanceAmount)
      return roundedTotalFlex <= roundedAdvanceAmount
    })
    .test('flexAmountMinimum', FLEX_AMOUNT_LESS_THAN_ADVANCED_AMOUNT, function (val) {
      const totalDisbursement = (this.parent.schedule as IFlexDisbursement[])
        .map(disbursement => Number(resolveGrossAmount(disbursement)) || 0)
        .reduce((a, b) => a + b, 0)
      const totalFlex = (Number(val) || 0) + totalDisbursement
      const advanceAmount = Number(this.parent.advanceAmount) || 0
      // Round to 2 decimal places to avoid floating point precision issues
      const roundedTotalFlex = mathRoundingAmount(totalFlex)
      const roundedAdvanceAmount = mathRoundingAmount(advanceAmount)
      return roundedTotalFlex >= roundedAdvanceAmount
    }),
})

export interface IFlexDisbursementForm {
  schedule: IFlexDisbursement[]
  advanceAmount: number
  flexAmount: number
}

export interface IFlexDisbursementFormValues extends IFlexDisbursementForm {
  dealStartDate: string | null
}

interface FlexDisbursementFormProps {
  initialValues: IFlexDisbursementForm
  initialDealStartDate?: string
  persistedDealStartDate?: string
  persistedSchedule?: IFlexDisbursement[]
  firstDisbursement: IFlexDisbursement
  onSubmit: (values: IFlexDisbursementFormValues) => void
  onResize: () => void
  bankFee?: number
  syndicationFeeDistribution?: IFeeDistributionOption
  uccUploaded?: boolean
  sendAsWire?: boolean
  extendType?: string
  disableDates?: boolean
}

const FlexDisbursementForm: FC<FlexDisbursementFormProps> = ({
  initialValues,
  initialDealStartDate,
  persistedDealStartDate,
  persistedSchedule,
  firstDisbursement,
  onSubmit,
  onResize,
  bankFee = 0,
  syndicationFeeDistribution,
  uccUploaded = false,
  sendAsWire = false,
  extendType,
  disableDates = false,
}) => {
  const today = new Date()
  const s = useStyles()
  const bankFeeGrossUpFactor = useMemo(
    () => getBankFeeGrossUpFactor(Number(initialValues.advanceAmount) || 0, bankFee, syndicationFeeDistribution, extendType),
    [initialValues.advanceAmount, bankFee, syndicationFeeDistribution, extendType]
  )
  const toDisplayAmount = useCallback(
    (amount?: number) => mathRoundingAmount((Number(amount) || 0) / bankFeeGrossUpFactor),
    [bankFeeGrossUpFactor]
  )
  const initialAmountsById = useMemo(
    () =>
      new Map(
        initialValues.schedule.map(disbursement => [
          disbursement.id,
          { stored: disbursement.amount, displayed: toDisplayAmount(disbursement.amount) },
        ])
      ),
    [initialValues.schedule, toDisplayAmount]
  )
  const resolveGrossAmount = useCallback(
    (disbursement: IFlexDisbursement) => {
      const displayed = mathRoundingAmount(Number(disbursement.amount) || 0)
      const initial = initialAmountsById.get(disbursement.id)

      return initial && initial.displayed === displayed && initial.stored !== undefined
        ? initial.stored
        : mathRoundingAmount(displayed * bankFeeGrossUpFactor)
    },
    [initialAmountsById, bankFeeGrossUpFactor]
  )
  const validationSchema = useMemo(
    () => buildValidationSchema(persistedDealStartDate, persistedSchedule, resolveGrossAmount),
    [persistedDealStartDate, persistedSchedule, resolveGrossAmount]
  )
  const formik = useFormik<IFlexDisbursementFormValues>({
    validationSchema,
    initialValues: {
      ...initialValues,
      schedule: initialValues.schedule.map(disbursement => ({
        ...disbursement,
        amount: disbursement.amount === undefined ? disbursement.amount : toDisplayAmount(disbursement.amount),
      })),
      dealStartDate: initialDealStartDate ?? null,
    },
    enableReinitialize: false,
    onSubmit: formValues =>
      onSubmit({
        ...formValues,
        schedule: formValues.schedule.map(disbursement => ({
          ...disbursement,
          amount: disbursement.amount === undefined ? disbursement.amount : resolveGrossAmount(disbursement),
        })),
      }),
  })

  const { values, touched, errors, submitCount, handleSubmit, handleChange, setFieldValue, setFieldTouched } = formik

  const createDisbursementWithId = (): IFlexDisbursement => {
    const maxId = values.schedule.reduce((max, obj) => Math.max(max, obj.id), 0)
    return { id: maxId + 1 }
  }

  const calculateNetAmount = (): number => {
    const disbursementAmount = Number(firstDisbursement.amount) || 0
    const advanceAmount = Number(values.advanceAmount) || 0
    const appliedBankFee =
      syndicationFeeDistribution === 'Distribute' && advanceAmount > 0 ? (bankFee * disbursementAmount) / advanceAmount : bankFee
    const uccFee = uccUploaded ? 200 : 0
    const wireFee = sendAsWire ? 50 : 0
    return disbursementAmount - appliedBankFee - uccFee - wireFee
  }

  const getTooltipMessage = (): string => {
    const netAmount = calculateNetAmount()
    return `Net Amount of this disbursement is equal to ${convertToPriceFormat(netAmount)}. ` +
      `Net amount is calculated by deducting Bank fee, UCC fee and Send as Wire fee from the disbursement.`
  }

  const handleDealStartDateChange = (value: MaterialUiPickersDate) => {
    const converted = convertDateForPicker(value) as Date | null
    setFieldValue('dealStartDate', converted && isValidDate(converted) ? converted.toISOString() : null)
  }

  return (
    <FormikProvider value={formik}>
      <form
        onSubmit={e => {
          setFieldTouched('schedule')
          handleSubmit(e)
        }}
      >
        <FieldArray
          name="schedule"
          render={({ push, remove }) => (
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <Box mb={3}>
                  <Typography variant="body1">Flex disbursements</Typography>
                  {extendType === 'Reverse' && syndicationFeeDistribution === 'Distribute' && (
                    <Typography variant="body2" color="error">
                      Please note that the Bank fee is spread across all disbursements. The first disbursement is shown including its
                      share of the Bank fee, the amounts below exclude it, and each share is deducted from its own disbursement when
                      it is paid out.
                    </Typography>
                  )}
                </Box>
              </Grid>
              {(extendType === 'Reverse' && (
                <Tooltip title={getTooltipMessage()} placement="top" arrow>
                  <Grid item xs={12}>
                    <Grid container spacing={6}>
                      <Grid item xs={4}>
                        <DatePickerWithTooltip
                          fullWidth
                          type="keyboard"
                          disablePast
                          disabled={disableDates}
                          label="Deal Start Date"
                          name="dealStartDate"
                          value={values.dealStartDate ? new Date(values.dealStartDate) : null}
                          onChange={handleDealStartDateChange}
                          error={Boolean(touched.dealStartDate && errors.dealStartDate)}
                          helperText={touched.dealStartDate && errors.dealStartDate}
                          tooltipText="The date when the deal officially starts, the deal terms become active and the first disbursement is made."
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <DecimalTextField
                          disabled
                          fullWidth
                          variant="outlined"
                          label="Amount"
                          value={firstDisbursement.amount ?? ''}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Button
                          disabled
                          fullWidth
                          startIcon={<PlusIcon className={`${s.icon} ${s.iconRemove}`} />}
                          variant="contained"
                          color="inherit"
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Tooltip>
              )) || (
                <Grid item xs={12}>
                  <Grid container spacing={6}>
                    <Grid item xs={4}>
                      <DatePickerWithTooltip
                        fullWidth
                        type="keyboard"
                        disablePast
                        disabled={disableDates}
                        label="Deal Start Date"
                        name="dealStartDate"
                        value={values.dealStartDate ? new Date(values.dealStartDate) : null}
                        onChange={handleDealStartDateChange}
                        error={Boolean(touched.dealStartDate && errors.dealStartDate)}
                        helperText={touched.dealStartDate && errors.dealStartDate}
                        tooltipText="The date when the deal officially starts, the deal terms become active and the first disbursement is made."
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <DecimalTextField
                        disabled
                        fullWidth
                        variant="outlined"
                        label="Amount"
                        value={firstDisbursement.amount ?? ''}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        disabled
                        fullWidth
                        startIcon={<PlusIcon className={`${s.icon} ${s.iconRemove}`} />}
                        variant="contained"
                        color="inherit"
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {values.schedule.map(({ id, date, amount }, index) => (
                <Fragment key={id}>
                  <Grid item xs={4}>
                    <DatePicker
                      fullWidth
                      type="keyboard"
                      disablePast
                      minDate={today}
                      label="Due Date"
                      name={`schedule[${index}].date`}
                      value={date || null}
                      onChange={x => setFieldValue(`schedule[${index}].date`, convertDateForPicker(x))}
                      error={Boolean((touched.schedule?.[index]?.date || submitCount > 0) && (errors.schedule?.[index] as any)?.date)}
                      helperText={(touched.schedule?.[index]?.date || submitCount > 0) && (errors.schedule?.[index] as any)?.date}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <DecimalTextField
                      fullWidth
                      variant="outlined"
                      label="Amount"
                      name={`schedule[${index}].amount`}
                      value={amount ?? ''}
                      onChange={handleChange}
                      error={Boolean(touched.schedule?.[index]?.amount && (errors.schedule?.[index] as any)?.amount)}
                      helperText={touched.schedule?.[index]?.amount && (errors.schedule?.[index] as any)?.amount}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Button
                      fullWidth
                      onClick={() => {
                        remove(index)
                        onResize()
                      }}
                      startIcon={<PlusIcon className={`${s.icon} ${s.iconRemove}`} />}
                      variant="contained"
                      color="inherit"
                    >
                      Remove
                    </Button>
                  </Grid>
                </Fragment>
              ))}
              <Grid item xs={12}>
                <Button
                  onClick={() => {
                    push(createDisbursementWithId())
                    onResize()
                  }}
                  startIcon={<PlusIcon className={s.icon} />}
                  variant="contained"
                  color="inherit"
                >
                  Add disbursement
                </Button>
              </Grid>
              <Grid item xs={12}>
                {Boolean(errors.flexAmount) && <FormHelperText error>{errors.flexAmount}</FormHelperText>}
                <Box mt={3}>
                  <Button type="submit" color="primary" variant="contained">
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        />
      </form>
    </FormikProvider>
  )
}

export default FlexDisbursementForm
